import { groupBy, isEqual, mapKeys, uniqBy } from 'lodash'
import {
  COLUMN_ROLE,
  GRID_ROLE,
  MEDIA_GALLERY_ROLE,
  REPEATER_ROLE,
  STRIP_ROLE,
  findError,
  isWixDataConflictError,
} from '@wix/wix-data-client-common-standalone'
import openConfirmationPanel from '@wix/wix-data-client-common/dist/esm/panels/error-panel/openConfirmationPanel'
import { digestSchema } from '@wix/wix-data-client-common/dist/esm/business-logic/collections/digestSchema'
import { getConnectableComponents } from './getConnectableComponents'
import removeRouterDatasetController from '../business-logic/datasets/removeDatasetController'
import openDatasetConfigPanel from '../panels/dataset-config-panel/openPanel'
import { updateControllersState } from './controllersState'
import DataBindingApi from './data-binding-api'
import { definitions as tpaActionDefinitions } from '@wix/data-binding-tpa-actions'
import {
  openAddFilterPanel,
  openEditFilterPanel,
} from '../panels/filter-panel/openPanel'
import {
  openAddSortPanel,
  openEditSortPanel,
} from '../panels/sort-panel/openPanel'
import { getAllowedFilterFields } from '../panels/dataset-config-panel/state/helpers'
import { openCreateDatasetPanel } from '../panels/create-dataset-panel/openPanel'
import { openFieldIdsPanel } from '../panels/field-ids-panel/openPanel'
import openChangeCollectionPanel from '../panels/change-collection-panel/openPanel'
import openAddFieldPanel from '../panels/add-field-panel/openPanel'
import openAddColumnPanel from '../panels/add-column-panel/openPanel'
import { disconnectUserFiltersAndTotalCount } from './disconnectUserFiltersAndTotalCount'
import {
  isInspectorPanelOpen,
  openClassicInspectorPanel,
} from '../openInspectorPanel'
import { setShowOnAllPages } from './setShowOnAllPages'
import { getPermissionsTemplate } from './utils/getPermissionTemplate'
import { getPermissionsTemplate as getPermissionsTemplateFromCMS } from '@wix/auto-cms-toolkit/permissions-template'
import { createViewerStatus } from './viewerStatus'
import {
  checkAllBindings,
  checkSomeBindings,
  DATASET,
  derivePermissionsInfoText,
  isClassicEditor,
  isDisplayElementBinding,
  isInputBinding,
  isUseInputFilter,
  runTransactionWithRetry,
  WIDGET_DATASET,
} from '@wix/wix-data-client-common'
import { ADMIN_PAGES } from '@wix/app-definition-ids'
import { getBindingDefinitions } from '../business-logic/bindings/componentBindingInfo'
import { FieldType } from '@wix/wix-data-schema-types'
import {
  rebindComponentsToAnotherCollection,
  rebindBuilderComponentsToAnotherCollection,
} from './data-binding-api/rebind-components'
import { fetchControllerBindings } from './data-binding-api/fetchControllerBindings'
import { replaceDatasetCollection } from './data-binding-api/replaceDatasetCollection'

export default ({
  experimentsManager,
  i18n,
  editorSdkProxy,
  collectionsApi,
  hostname,
  logger,
  wixData,
  finalAppApiPromise,
  bindingDefinitionProviderCreator,
  httpClient,
  bindingDefinitionProvider = bindingDefinitionProviderCreator({
    editorSdkProxy,
    i18n,
    logger,
  }),
  editorType,
  features,
}) => {
  let connectableComponentsPromise
  const isObjectBindingEnabled = experimentsManager.enabled(
    'specs.wixDataClient.objectBinding',
  )

  const dataBindingApi = DataBindingApi({
    i18n,
    editorSdkProxy,
    collectionsApi,
    editorType,
    experimentsManager,
    features,
  })

  async function ifStripHasSingleColumnFindConnectionWithColumnRoleAndDefinedConfigurationOfThatColumn({
    stripRef,
  }) {
    const columnRefs = await editorSdkProxy.components.getChildren({
      componentRef: stripRef,
    })
    const { length: numberOfColumns } = columnRefs
    if (numberOfColumns !== 1) {
      return
    }

    const [singleColumnRef] = columnRefs
    const singleColumnConnections =
      await editorSdkProxy.controllers.listConnections({
        componentRef: singleColumnRef,
      })
    const singleColumnConnectionWithColumnRoleAndDefinedConfiguration =
      singleColumnConnections.find(
        singleColumnConnection =>
          singleColumnConnection.role === COLUMN_ROLE &&
          typeof singleColumnConnection.config !== 'undefined',
      )
    return singleColumnConnectionWithColumnRoleAndDefinedConfiguration
  }

  async function findDataConnectionRequiringPrimariness({
    connectionsOfComponent,
    getBindingDefinitionsOfComponent,
  }) {
    const itemContainerConnection = connectionsOfComponent.find(
      connection =>
        connection.role === GRID_ROLE ||
        connection.role === MEDIA_GALLERY_ROLE ||
        connection.role === REPEATER_ROLE,
    )
    const thereIsItemContainerConnection =
      typeof itemContainerConnection !== 'undefined'
    if (thereIsItemContainerConnection) {
      return itemContainerConnection
    }

    const bindingDefinitions = await getBindingDefinitionsOfComponent()
    if (!bindingDefinitions) {
      return
    }

    // There can be only one binding requiring primary connection.
    // "Primary binding" is a shortening for "binding requiring primary connection".
    const sectionWithPrimaryBinding = bindingDefinitions.sections.find(
      section =>
        section.bindings.some(binding => binding.requiresPrimaryConnection),
    )
    const thereIsNoSectionWithPrimaryBinding =
      typeof sectionWithPrimaryBinding === 'undefined'
    if (thereIsNoSectionWithPrimaryBinding) {
      return
    }

    const primaryBinding = sectionWithPrimaryBinding.bindings.find(
      binding => binding.requiresPrimaryConnection,
    )
    const connectionForSectionWithPrimaryBinding = connectionsOfComponent.find(
      connection => connection.role === sectionWithPrimaryBinding.role,
    )
    const thereIsNoConnectionForSectionWithPrimaryBinding =
      typeof connectionForSectionWithPrimaryBinding === 'undefined'
    if (thereIsNoConnectionForSectionWithPrimaryBinding) {
      return
    }

    const {
      config: {
        properties: {
          [primaryBinding.prop]: configurationForPrimaryBinding,
        } = {},
      } = {},
    } = connectionForSectionWithPrimaryBinding
    const primaryBindingIsUnconfigured =
      typeof configurationForPrimaryBinding === 'undefined'
    if (primaryBindingIsUnconfigured) {
      return
    }

    return connectionForSectionWithPrimaryBinding
  }

  const openResponsiveInspectorPanel = async () =>
    editorSdkProxy.editor.deeplink.show({
      type: 'inspectorPanel',
      params: ['data'],
    })

  const hiddenControllers = []

  let bindingSuggestionPreventionCount = 0

  const viewerStatus = createViewerStatus()
  return {
    isControllerHidden(controllerRef) {
      return !!hiddenControllers.find(ref => isEqual(ref, controllerRef))
    },

    async hideControllers(controllerRefs) {
      controllerRefs.forEach(controllerRef => {
        if (!this.isControllerHidden(controllerRef)) {
          hiddenControllers.push(controllerRef)
        }
      })
      await this.refreshControllersState({ controllerRefs })
    },

    getConnectableComponents() {
      return connectableComponentsPromise
    },

    updateConnectableComponentsPromise({ controllerRef }) {
      connectableComponentsPromise = getConnectableComponents({
        editorSdkProxy,
        controllerRef,
        experimentsManager,
      })
    },

    async showLivePreviewPageSizeNotification() {
      await editorSdkProxy.editor.showPresetUserActionNotification({
        preset:
          editorSdkProxy.editor.NotificationPresetTypes
            .REPEATER_EDITOR_MAX_ITEMS,
      })
    },

    async getBindingDefinition({ componentType }) {
      return bindingDefinitionProvider.get(componentType)
    },

    async getAllBindingDefinitions() {
      return bindingDefinitionProvider.getAll()
    },

    async removeRouterDatasetController({ datasetRef }) {
      return removeRouterDatasetController({ editorSdkProxy, datasetRef })
    },

    async openInspectorPanel({
      view,
      componentRef,
      pageRef: _pageRef,
      initiator = 'manually',
      origin = 'gfpp',
    }) {
      const appApi = await finalAppApiPromise

      appApi.emit('openInspectorPanel', { origin })

      if (!isClassicEditor(editorType)) {
        return openResponsiveInspectorPanel()
      }

      const [isOpen, pageRef] = await Promise.all([
        isInspectorPanelOpen(appApi),
        _pageRef || editorSdkProxy.pages.getCurrent(),
      ])

      const initialView = view && {
        view,
        componentRef,
        pageRef,
      }

      if (isOpen) {
        return appApi.emit('updateInspectorView', initialView)
      }

      return openClassicInspectorPanel(
        {
          hostname,
          appApi,
          editorSdkProxy,
          experimentsManager,
          logger,
          i18n,
          initiator,
          origin,
        },
        {
          componentRef,
          initialView,
        },
      )
    },

    async openDatasetConfigPanel({
      controllerRef,
      controllerConfig,
      controllerDisplayName,
      controllerType,
    }) {
      if (features.inspectorXP) {
        const [appApi, currentPage] = await Promise.all([
          finalAppApiPromise,
          editorSdkProxy.pages.getCurrent(),
          editorSdkProxy.selection.selectComponentByCompRef({
            compsToSelect: [controllerRef],
          }),
        ])

        return appApi.openInspectorPanel({
          view: 'DatasetSettings',
          componentRef: controllerRef,
          pageRef: currentPage,
        })
      }

      return openDatasetConfigPanel(
        {
          hostname,
          collectionsApi,
          editorSdkProxy,
          appApi: dataBindingApi,
          i18n,
        },
        {
          controllerRef,
          controllerConfig,
          controllerDisplayName,
          controllerType,
          editorType,
        },
      )
    },

    async openInspectorBindingPanel({ componentRef }) {
      const [appApi, currentPage] = await Promise.all([
        finalAppApiPromise,
        editorSdkProxy.pages.getCurrent(),
        editorSdkProxy.selection.selectComponentByCompRef({
          compsToSelect: [componentRef],
        }),
      ])

      return appApi.openInspectorPanel({
        view: 'Connect',
        componentRef,
        pageRef: currentPage,
      })
    },

    async openInspectorDatasetListPanel() {
      const [appApi, currentPage] = await Promise.all([
        finalAppApiPromise,
        editorSdkProxy.pages.getCurrent(),
      ])
      editorSdkProxy.selection.selectComponentByCompRef({
        compsToSelect: [currentPage],
      })

      return appApi.openInspectorPanel({
        view: 'DatasetList',
        componentRef: currentPage,
        pageRef: currentPage,
      })
    },

    async openAddFilterPanel({ collections, dataset }) {
      const selectedCollection = await collectionsApi.get(
        dataset.config.collectionId,
      )

      const digestedSelectedCollection = digestSchema({
        rawSchema: selectedCollection,
        shouldProcessObjectFields: isObjectBindingEnabled,
      })
      const allowedFields = getAllowedFilterFields(
        digestedSelectedCollection.fields,
        {
          controllerType: dataset.type,
        },
      )
      return openAddFilterPanel(
        {
          hostname,
          editorSdkProxy,
          experimentsManager,
          i18n,
          editorType,
          appApi: dataBindingApi,
        },
        {
          fields: allowedFields,
          controllerRef: dataset.controllerRef,
          controllerType: dataset.type,
          collections: mapKeys(
            collections.map(schema =>
              digestSchema({
                rawSchema: schema,
                shouldProcessObjectFields: isObjectBindingEnabled,
              }),
            ),
            col => col.id,
          ),
          isTPACollection: digestedSelectedCollection.isTPACollection,
          selectedCollection: digestedSelectedCollection,
        },
      )
    },

    async openAddFilterPanelForDynamicMenu({ collectionId, controllerRef }) {
      const selectedCollection = await collectionsApi.get(collectionId)
      const digestedSelectedCollection = digestSchema({
        rawSchema: selectedCollection,
        shouldProcessObjectFields: isObjectBindingEnabled,
      })
      // Note: Should include same fields as for DATASET controller but without ref and multiref
      const allowedFields = getAllowedFilterFields(
        digestedSelectedCollection.fields,
        {
          controllerType: DATASET,
        },
      ).filter(
        field =>
          ![FieldType.reference, FieldType.multiReference].includes(field.type),
      )

      return openAddFilterPanel(
        {
          hostname,
          editorSdkProxy,
          experimentsManager,
          i18n,
          editorType,
          appApi: dataBindingApi,
        },
        {
          origin: 'menu',
          fields: allowedFields,
          controllerRef,
          controllerType: controllerRef ? DATASET : null,
          collections: {},
          isTPACollection: digestedSelectedCollection.isTPACollection,
          selectedCollection: digestedSelectedCollection,
        },
      )
    },

    async openEditFilterPanel({ collections, dataset, filterExpression }) {
      const selectedCollection = await collectionsApi.get(
        dataset.config.collectionId,
      )
      const digestedSelectedCollection = digestSchema({
        rawSchema: selectedCollection,
        shouldProcessObjectFields: isObjectBindingEnabled,
      })
      const allowedFields = getAllowedFilterFields(
        digestedSelectedCollection.fields,
        {
          controllerType: dataset.type,
        },
      )
      return openEditFilterPanel(
        {
          hostname,
          editorSdkProxy,
          i18n,
          experimentsManager,
          editorType,
          appApi: dataBindingApi,
        },
        {
          fields: allowedFields,
          controllerRef: dataset.controllerRef,
          controllerType: dataset.type,
          collections: mapKeys(
            collections.map(schema =>
              digestSchema({
                rawSchema: schema,
                shouldProcessObjectFields: isObjectBindingEnabled,
              }),
            ),
            col => col.id,
          ),
          isTPACollection: digestedSelectedCollection.isTPACollection,
          selectedCollection: digestedSelectedCollection,
          filterExpression,
        },
      )
    },

    async openEditFilterPanelForDynamicMenu({
      collectionId,
      filterExpression,
      controllerRef,
    }) {
      const selectedCollection = await collectionsApi.get(collectionId)
      const digestedSelectedCollection = digestSchema({
        rawSchema: selectedCollection,
        shouldProcessObjectFields: isObjectBindingEnabled,
      })
      const allowedFields = getAllowedFilterFields(
        digestedSelectedCollection.fields,
        {
          controllerType: DATASET,
        },
      ).filter(
        field =>
          ![FieldType.reference, FieldType.multiReference].includes(field.type),
      )

      return openEditFilterPanel(
        {
          hostname,
          editorSdkProxy,
          experimentsManager,
          i18n,
          editorType,
          appApi: dataBindingApi,
        },
        {
          origin: 'menu',
          fields: allowedFields,
          controllerRef,
          controllerType: controllerRef ? DATASET : null,
          collections: {},
          isTPACollection: digestedSelectedCollection.isTPACollection,
          selectedCollection: digestedSelectedCollection,
          filterExpression,
        },
      )
    },

    async openAddSortPanel({
      fields,
      controllerRef,
      controllerType,
      collectionId,
      sortIsMirrored,
      hasSorts,
    }) {
      return openAddSortPanel(
        { hostname, editorSdkProxy, i18n, editorType },
        {
          fields,
          controllerRef,
          controllerType,
          collectionId,
          sortIsMirrored,
          hasSorts,
        },
      )
    },

    async openEditSortPanel({
      fields,
      controllerRef,
      controllerType,
      sortExpression,
      collectionId,
      sortIsMirrored,
    }) {
      return openEditSortPanel(
        { hostname, editorSdkProxy, i18n, editorType },
        {
          fields,
          controllerRef,
          controllerType,
          sortExpression,
          collectionId,
          sortIsMirrored,
        },
      )
    },

    async openCreateDatasetPanel({ componentRef, isDatasetTargetingInput }) {
      return openCreateDatasetPanel(
        {
          hostname,
          editorSdkProxy,
          i18n,
          experimentsManager,
        },
        {
          componentRef,
          isDatasetTargetingInput,
        },
      )
    },

    openChangeCollectionConfirmationPanel({
      controllerRef,
      newCollection,
      isPreviousCollectionHidden,
    }) {
      return openChangeCollectionPanel(
        { editorSdkProxy, i18n, logger },
        { controllerRef, newCollection, isPreviousCollectionHidden },
      )
    },

    async openAddColumnPanel({
      fields,
      componentType,
      componentRef,
      boundFields,
      repeaterAncestorCompRef,
    }) {
      return openAddColumnPanel(
        { hostname, editorSdkProxy, i18n },
        {
          fields,
          componentType,
          componentRef,
          boundFields,
          repeaterAncestorCompRef,
        },
      )
    },

    async updateMainActionOfStrip({ stripRef }) {
      const stripConnections = await editorSdkProxy.controllers.listConnections(
        { componentRef: stripRef },
      )
      const stripConnectionWithStripRole = stripConnections.find(
        stripConnection => stripConnection.role === STRIP_ROLE,
      )
      const stripHasConnectionWithStripRole =
        typeof stripConnectionWithStripRole !== 'undefined'
      const stripHasPrimaryConnectionWithNonStripRole =
        !stripHasConnectionWithStripRole &&
        stripConnections.some(stripConnection => stripConnection.isPrimary)
      const stripHasConnectionWithColumnRole = stripConnections.some(
        stripConnection => stripConnection.role === COLUMN_ROLE,
      )
      if (
        stripHasPrimaryConnectionWithNonStripRole ||
        stripHasConnectionWithColumnRole
      ) {
        return
      }

      const singleColumnConnectionWithColumnRoleAndDefinedConfiguration =
        await ifStripHasSingleColumnFindConnectionWithColumnRoleAndDefinedConfigurationOfThatColumn(
          { stripRef },
        )
      // Columns have only one bindable property—background.
      // When the background of a column is bound to data, the connection has a defined configuration.
      // When the property is not bound but the column is connected to a dataset, the configuration is undefined.
      // This is a simplified check for the binding:
      // since there can be only one binding, the presence of a configuration in the connection implies the existence of the binding.
      const stripHasSingleColumnThatIsBoundToData =
        typeof singleColumnConnectionWithColumnRoleAndDefinedConfiguration !==
        'undefined'

      if (
        stripHasSingleColumnThatIsBoundToData &&
        !stripHasConnectionWithStripRole
      ) {
        await editorSdkProxy.controllers.connect({
          controllerRef:
            singleColumnConnectionWithColumnRoleAndDefinedConfiguration.controllerRef,
          connectToRef: stripRef,
          role: STRIP_ROLE,
          isPrimary: true,
        })
      } else if (
        !stripHasSingleColumnThatIsBoundToData &&
        stripHasConnectionWithStripRole
      ) {
        await editorSdkProxy.controllers.disconnect({
          controllerRef: stripConnectionWithStripRole.controllerRef,
          connectToRef: stripRef,
          role: STRIP_ROLE,
        })
      }
    },

    async makeDataConnectionOfComponentPrimaryIfNeeded({ componentRef }) {
      const [component] = await editorSdkProxy.components.get({
        componentRefs: [componentRef],
        properties: ['connections', 'sdkType'],
      })
      const componentHasPrimaryConnection = component.connections.some(
        connection => connection.isPrimary,
      )
      if (componentHasPrimaryConnection) {
        return
      } // There is nothing to do because the component already has some primary connection.

      const dataConnectionRequiringPrimariness =
        await findDataConnectionRequiringPrimariness({
          connectionsOfComponent: component.connections,
          getBindingDefinitionsOfComponent: async () => {
            const bindingDefinitionsOfComponent =
              await bindingDefinitionProvider.get(component.sdkType)
            return bindingDefinitionsOfComponent
          },
        })
      const thereIsNoDataConnectionRequiringPrimariness =
        typeof dataConnectionRequiringPrimariness === 'undefined'
      if (thereIsNoDataConnectionRequiringPrimariness) {
        return
      }

      await editorSdkProxy.controllers.connect({
        connectToRef: componentRef,
        controllerRef: dataConnectionRequiringPrimariness.controllerRef,
        role: dataConnectionRequiringPrimariness.role,
        connectionConfig: dataConnectionRequiringPrimariness.config,
        isPrimary: true,
      })
    },

    async checkIfErrorIsConcurrentEditingError(error) {
      return !!findError(error, isWixDataConflictError)
    },

    async handleConcurrentEditingError() {
      openConfirmationPanel({
        editorSdkProxy,
        headerText: i18n.t('Concurrent_Editing_Error_Panel_Header_Text'),
        descriptionText: i18n.t(
          'Concurrent_Editing_Error_Panel_Description_Text',
        ),
        mainActionText: i18n.t(
          'Concurrent_Editing_Error_Panel_Main_Action_Text',
        ),
        logger,
      })
    },

    async refreshControllersState({ controllerRefs = null } = {}) {
      const appApi = await finalAppApiPromise
      return updateControllersState({
        appApi,
        collectionsApi,
        controllerRefsToUpdate: controllerRefs,
        editorSdkProxy,
      })
    },

    async disconnectUserInputFiltersOnChangeToWriteMode({ controllerRef }) {
      const appApi = await finalAppApiPromise

      const componentRefs =
        await editorSdkProxy.controllers.listConnectedComponents({
          controllerRef,
        })

      const disconnectedComponentRefs =
        await disconnectUserFiltersAndTotalCount({
          editorSdkProxy,
          appApi,
          componentRefs,
          insideRepeaterOnly: false,
        })

      return disconnectedComponentRefs
    },

    async openRemoveDatasetConfirmationPanel({
      datasetType = DATASET,
      isDatasetRemovable = true,
    } = {}) {
      if (datasetType === WIDGET_DATASET) {
        return isDatasetRemovable
          ? editorSdkProxy.editor.openConfirmationPanel({
              headerText: i18n.t('removeWidgetDatasetPanel.title'),
              descriptionText: i18n.t('removeWidgetDatasetPanel.description'),
              mainActionText: i18n.t('removeWidgetDatasetPanel.confirm'),
              secondaryActionText: i18n.t('removeWidgetDatasetPanel.cancel'),
            })
          : editorSdkProxy.editor.openConfirmationPanel({
              headerText: i18n.t('removeWidgetDatasetPanel.nonRemovable.title'),
              descriptionText: i18n.t(
                'removeWidgetDatasetPanel.nonRemovable.description',
              ),
              mainActionText: i18n.t(
                'removeWidgetDatasetPanel.nonRemovable.confirm',
              ),
            })
      }
      if (datasetType === DATASET) {
        return editorSdkProxy.editor.openConfirmationPanel({
          headerText: i18n.t('Remove_Dataset_Panel_Title'),
          descriptionText: i18n.t('Remove_Dataset_Panel_Paragraph1'),
          mainActionText: i18n.t('Remove_Dataset_Panel_ConfirmButton_Label'),
          secondaryActionText: i18n.t(
            'Remove_Dataset_Panel_CancelButton_Label',
          ),
        })
      }
      throw new Error(`Unsupported dataset type: ${datasetType}`)
    },

    async getItemById({ collectionId, itemId }) {
      return wixData.get(collectionId, itemId)
    },

    async getNickname({ componentRef }) {
      return editorSdkProxy.components.code.getNickname({ componentRef })
    },

    async setNickname({ componentRef, nickname }) {
      return editorSdkProxy.components.code.setNickname({
        componentRef,
        nickname,
      })
    },

    async openAddFieldPanel({
      fieldTypes,
      selectedFieldType,
      collections,
      fields,
      collectionName,
      origin,
    }) {
      return openAddFieldPanel({
        hostname,
        editorSdkProxy,
        i18n,
        fieldTypes,
        selectedFieldType,
        collections,
        fields,
        collectionName,
        origin,
      })
    },

    async openFieldIdsPanel(initialData) {
      return openFieldIdsPanel({
        initialData,
        editorSdkProxy,
        hostname,
        i18n,
      })
    },

    setShowOnAllPages: ({ componentRef, value }) => {
      return setShowOnAllPages({
        componentRef,
        newValue: value,
        editorSdkProxy,
        i18n,
      })
    },

    getPageData: pageRef => {
      return editorSdkProxy.document.pages.data.get({ pageRef })
    },

    evaluatePermissions: async ({
      pageRef,
      collection,
      isRouterDataset,
      controllerBindings,
    }) => {
      if (!collection) {
        return
      }

      const [pageData, isDynamicPage, appApi] = await Promise.all([
        editorSdkProxy.document.pages.data.get({ pageRef }),
        editorSdkProxy.document.pages.isDynamicPage({ pageRef }),
        editorSdkProxy.editor.getAppAPI(),
      ])

      const isAdminDashboardPage = pageData.managingAppDefId === ADMIN_PAGES
      if (isAdminDashboardPage) {
        return
      }

      const controllerBindingsByComponentRefId = groupBy(
        controllerBindings,
        'componentRef.id',
      )

      const componentRefs = uniqBy(
        controllerBindings.map(({ componentRef }) => componentRef),
        'id',
      )

      const componentTypes = await editorSdkProxy.components.get({
        componentRefs,
        properties: ['sdkType', 'componentType'],
      })

      const bindingsWithDefinitions = (
        await Promise.all(
          componentRefs.map(async componentRef => {
            const { sdkType, componentType } = componentTypes.find(
              componentType =>
                componentType.componentRef.id === componentRef.id,
            )

            const bindingDefinition = await getBindingDefinitions({
              editorSdkProxy,
              appApi,
              componentRef,
              componentType: sdkType,
              componentViewerType: componentType,
            })

            return {
              componentRef,
              bindingDefinition: bindingDefinition.getOrElse(null),
              connections: controllerBindingsByComponentRefId[componentRef.id],
            }
          }),
        )
      ).filter(({ bindingDefinition }) => bindingDefinition !== null)

      const hasInputBindings = checkSomeBindings({
        bindingsWithDefinitions,
        predicate: isInputBinding,
      })

      const hasDisplayElementBindings = checkSomeBindings({
        bindingsWithDefinitions,
        predicate: isDisplayElementBinding,
      })

      const allInputBindingsAreUserInputFilters = checkAllBindings({
        bindingsWithDefinitions,
        predicate: isUseInputFilter,
        filterFn: isInputBinding,
      })

      const hasUserInputFilters = checkSomeBindings({
        bindingsWithDefinitions,
        predicate: isUseInputFilter,
      })

      const isMembersOnlyPage = Boolean(pageData.pageSecurity?.requireLogin)

      const collectionPermissions = collection.permissions

      const isConnectedToWixAppCollection = Boolean(collection.ownerAppId)

      return derivePermissionsInfoText({
        collectionPermissions,
        hasInputBindings,
        hasDisplayElementBindings,
        hasUserInputFilters,
        allInputBindingsAreUserInputFilters,
        isDynamicPage,
        isRouterDataset,
        isMembersOnlyPage,
        isConnectedToWixAppCollection,
      })
    },

    getPermissionsTemplate: async ({ collection }) => {
      const template = await getPermissionsTemplate(httpClient, collection.id)

      const permissionsTemplate = await getPermissionsTemplateFromCMS(
        template,
        collection.permissions,
      )

      return permissionsTemplate
    },

    setDatasetDisplayName: dataBindingApi.setDatasetDisplayName,
    updateDatasetConfig: dataBindingApi.updateDatasetConfig,
    changePresetDatasetCollection: dataBindingApi.changePresetDatasetCollection,
    updateDatasetWithCreateDatasetPanelResult:
      dataBindingApi.updateDatasetWithCreateDatasetPanelResult,
    fetchDataset: dataBindingApi.fetchDataset,
    fetchConnectableDatasets: dataBindingApi.fetchConnectableDatasets,
    connectBindingSection: dataBindingApi.connectBindingSection,
    disconnectBindingSection: dataBindingApi.disconnectBindingSection,
    rebindComponentsToAnotherCollection: async ({
      fieldMap,
      controllerRef,
      newCollectionId,
      respectMainProperty,
    }) => {
      const appApi = await finalAppApiPromise

      return rebindComponentsToAnotherCollection({
        fieldMap,
        controllerRef,
        newCollectionId,
        collectionsApi,
        appApi,
        respectMainProperty,
        editorSdkProxy,
      })
    },
    rebindBuilderComponentsToAnotherCollection: async ({
      fieldMap,
      controllerRef,
      newCollectionId,
    }) => {
      return rebindBuilderComponentsToAnotherCollection({
        fieldMap,
        controllerRef,
        editorSdkProxy,
        newCollectionId,
        collectionsApi,
      })
    },
    fetchComponentBindings: dataBindingApi.fetchComponentBindings,
    fetchControllerBindings: ({ controllerRef }) =>
      fetchControllerBindings({
        editorSdkProxy,
        controllerRef,
      }),
    fetchConnectedDataset: dataBindingApi.fetchConnectedDataset,
    fetchRepeaterChildrenBindings: dataBindingApi.fetchRepeaterChildrenBindings,
    createDataset: dataBindingApi.createDataset,
    replaceDatasetCollection: async ({
      newCollectionId,
      fieldMap,
      controllerRef,
    }) => {
      const appApi = await finalAppApiPromise
      const newCollectionSchema = await collectionsApi.get(newCollectionId)

      await runTransactionWithRetry(editorSdkProxy, async () =>
        replaceDatasetCollection({
          controllerRef,
          newCollectionSchema,
          fieldMap,
          appApi,
        }),
      )
    },
    duplicateDataset: dataBindingApi.duplicateDataset,
    saveTableColumns: dataBindingApi.saveTableColumns,
    removeTableColumns: dataBindingApi.removeTableColumns,
    getTpaActionDefinitions: () => tpaActionDefinitions,
    addSubmitFeedbackMessage: dataBindingApi.addSubmitFeedbackMessage,
    getLinkAsString: link =>
      editorSdkProxy.editor.utils.getLinkAsString({ link }),

    bindingSuggestionIsPrevented: () => bindingSuggestionPreventionCount !== 0,
    withPreventedBindingSuggestion: async runAction => {
      bindingSuggestionPreventionCount += 1
      try {
        await runAction()
      } finally {
        bindingSuggestionPreventionCount -= 1
      }
    },
    getViewerStatus: () => Promise.resolve(viewerStatus.get()),
    setViewerStatusMessage: async _viewerStatusMessage => {
      if (!viewerStatus.update(_viewerStatusMessage)) {
        return
      }
      const appApi = await finalAppApiPromise
      await appApi.emit('viewerStatusChanged', viewerStatus.get())
    },
  }
}
